import React, { useEffect, useRef, useState } from "react"
import "./DataCompletionDashboard.scss"
import { useNavigate } from "react-router-dom"
import DataCompletionApi from "Api/DataCompletionApi"
import {
    Button,
    Col,
    Input,
    Pagination,
    PaginationItem,
    PaginationLink,
    Row,
    Table,
    Card,
    CardBody,
    Badge,
} from "reactstrap"
import CustomBreadCrumb from "utils/CustomBreadCrumb/CustomBreadCrumb"
import DataTableLoader from "SkeletonLoader/DataTableLoader"
import { Toast } from "primereact/toast"

const DataCompletionDashboard = () => {
    document.title = "Data Completion Dashboard"
    const navigate = useNavigate()
    const accessToken = localStorage.getItem("accessToken")
    const toast = useRef(null)

    // State
    const [institutions, setInstitutions] = useState([])
    const [loading, setLoading] = useState(true)
    const [page, setPage] = useState(1)
    const [limit, setLimit] = useState(20)
    const [totalRecords, setTotalRecords] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [statistics, setStatistics] = useState(null)

    // Filters
    const [category, setCategory] = useState("all")
    const [missingPrograms, setMissingPrograms] = useState(true)
    const [missingCourses, setMissingCourses] = useState(true)
    const [searchQuery, setSearchQuery] = useState("")



    const breadcrumbItems = [
        { label: "Pixwik", link: "/" },
        { label: "Data Completion", link: "#" },
        { label: "Dashboard", link: "#" },
    ]

    const categoryOptions = [
        { name: "All Categories", code: "all" },
        { name: "Medical", code: "medical" },
        { name: "Engineering", code: "engineering" },
        { name: "Arts & Science", code: "artsAndScience" },
        { name: "Agriculture", code: "agriculture" },
        { name: "Law", code: "law" },
        { name: "Management", code: "management" },
    ]

    const fetchData = () => {
        setLoading(true)

        const config = {
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
            params: {
                category,
                missingPrograms: missingPrograms ? "true" : "false",
                missingCourses: missingCourses ? "true" : "false",
                search: searchQuery,
                page,
                limit,
            },
        }

        DataCompletionApi.getIncompleteInstitutions(config)
            .then(response => {
                setLoading(false)
                if (response.data.status) {
                    setInstitutions(response.data.data || [])
                    setTotalRecords(response.data.pagination?.totalRecords || 0)
                    setTotalPages(response.data.pagination?.totalPages || 0)
                    setStatistics(response.data.statistics || null)
                } else {
                    setInstitutions([])
                    setTotalRecords(0)
                    setTotalPages(0)
                    toast.current.show({
                        severity: "info",
                        summary: "No Data",
                        detail: response.data.message || "No incomplete institutions found",
                        life: 3000,
                    })
                }
            })
            .catch(error => {
                setLoading(false)
                setInstitutions([])
                setTotalRecords(0)
                setTotalPages(0)
                toast.current.show({
                    severity: "error",
                    summary: "Error",
                    detail: error.response?.data?.message || "Failed to fetch data",
                    life: 3000,
                })
            })
    }

    useEffect(() => {
        fetchData()
    }, [category, missingPrograms, missingCourses, searchQuery, page, limit])

    const handleRefresh = () => {
        toast.current.show({
            severity: "info",
            summary: "Refreshing",
            detail: "Fetching latest data...",
            life: 2000,
        })
        fetchData()
    }

    const handleAddProgram = institution => {
        console.log('=== Data Completion Dashboard - Add Program Clicked ===')
        console.log('Institution data:', institution)

        const stateData = {
            institutionCode: institution.institutionCode,
            institutionId: institution._id,
            institutionName: institution.institutionName,
            universityId: institution.university?._id || "",
            universityCode: institution.university?.universityCode || "",
            universityName: institution.university?.universityName || "",
            category: institution.category,
            returnToDashboard: true, // Flag to return to dashboard after adding
        }

        console.log('State being passed to Add Program:', stateData)

        navigate("/addProgram", {
            state: stateData,
        })
    }

    const handleAddCourse = institution => {
        if (institution.programCount === 0) {
            toast.current.show({
                severity: "warn",
                summary: "Warning",
                detail: "Please add at least one program first",
                life: 3000,
            })
            return
        }

        console.log('=== Data Completion Dashboard - Add Course Clicked ===')
        console.log('Institution data:', institution)

        const stateData = {
            institutionCode: institution.institutionCode,
            institutionId: institution._id,
            institutionName: institution.institutionName,
            universityId: institution.university?._id || "",
            universityCode: institution.university?.universityCode || "",
            universityName: institution.university?.universityName || "",
            category: institution.category,
            returnToDashboard: true, // Flag to return to dashboard after adding
        }

        console.log('State being passed to Add Course:', stateData)

        navigate("/addCourse", {
            state: stateData,
        })
    }



    return (
        <div className="data-completion-dashboard">
            <Toast ref={toast} />

            <Row>
                <Col xs="12">
                    <div className="dashboard-header">
                        <CustomBreadCrumb data={breadcrumbItems} pageTitle="Data Completion Dashboard" />

                        <div className="d-flex align-items-center gap-3 mt-3">
                            <Button
                                color="primary"
                                onClick={handleRefresh}
                                disabled={loading}
                            >
                                <i className="mdi mdi-refresh me-1" />
                                Refresh Data
                            </Button>
                        </div>
                    </div>

                    {/* Statistics Cards */}
                    {statistics && (
                        <Row className="mt-4">
                            <Col md="6">
                                <Card className="stats-card">
                                    <CardBody>
                                        <div className="stats-content">
                                            <div className="stats-icon bg-danger">
                                                <i className="mdi mdi-school" />
                                            </div>
                                            <div className="stats-info">
                                                <h5>{statistics.institutionsWithNoPrograms}</h5>
                                                <p>Institutions without Programs</p>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                            <Col md="6">
                                <Card className="stats-card">
                                    <CardBody>
                                        <div className="stats-content">
                                            <div className="stats-icon bg-warning">
                                                <i className="mdi mdi-book-open-variant" />
                                            </div>
                                            <div className="stats-info">
                                                <h5>{statistics.institutionsWithNoCourses}</h5>
                                                <p>Institutions without Courses</p>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>
                    )}

                    {/* Filters */}
                    <Card className="mt-4">
                        <CardBody>
                            {missingPrograms && missingCourses && (
                                <div className="alert alert-info mb-3" role="alert">
                                    <i className="mdi mdi-information me-2"></i>
                                    Showing institutions with <strong>either</strong> 0 programs <strong>or</strong> 0 courses (or both)
                                </div>
                            )}
                            <Row className="align-items-center">
                                <Col md="3">
                                    <label>Category</label>
                                    <Input
                                        type="select"
                                        value={category}
                                        onChange={e => {
                                            setCategory(e.target.value)
                                            setPage(1)
                                        }}
                                    >
                                        {categoryOptions.map((cat, index) => (
                                            <option key={index} value={cat.code}>
                                                {cat.name}
                                            </option>
                                        ))}
                                    </Input>
                                </Col>
                                <Col md="3">
                                    <label>Search</label>
                                    <Input
                                        type="text"
                                        placeholder="Search institutions..."
                                        value={searchQuery}
                                        onChange={e => {
                                            setSearchQuery(e.target.value)
                                            setPage(1)
                                        }}
                                    />
                                </Col>
                                <Col md="3">
                                    <div className="d-flex align-items-center gap-2 mt-4">
                                        <Input
                                            type="checkbox"
                                            checked={missingPrograms}
                                            onChange={e => {
                                                setMissingPrograms(e.target.checked)
                                                setPage(1)
                                            }}
                                            id="missingPrograms"
                                        />
                                        <label htmlFor="missingPrograms" className="mb-0">
                                            Show 0 Programs
                                        </label>
                                    </div>
                                </Col>
                                <Col md="3">
                                    <div className="d-flex align-items-center gap-2 mt-4">
                                        <Input
                                            type="checkbox"
                                            checked={missingCourses}
                                            onChange={e => {
                                                setMissingCourses(e.target.checked)
                                                setPage(1)
                                            }}
                                            id="missingCourses"
                                        />
                                        <label htmlFor="missingCourses" className="mb-0">
                                            Show 0 Courses
                                        </label>
                                    </div>
                                </Col>
                            </Row>
                        </CardBody>
                    </Card>

                    {/* Data Table */}
                    <div className="table-responsive mt-4 mb-3">
                        {loading ? (
                            <DataTableLoader cols={9} />
                        ) : institutions.length === 0 ? (
                            <Card>
                                <CardBody className="text-center py-5">
                                    <i className="mdi mdi-check-circle text-success" style={{ fontSize: "48px" }} />
                                    <h4 className="mt-3">Great! No Incomplete Data Found</h4>
                                    <p className="text-muted">All institutions matching your filters have complete data.</p>
                                </CardBody>
                            </Card>
                        ) : (
                            <>
                                <Table className="table mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>#</th>
                                            <th>Institution Name</th>
                                            <th>Category</th>
                                            <th>Location</th>
                                            <th>Programs</th>
                                            <th>Courses</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {institutions.map((institution, index) => (
                                            <tr key={institution._id}>
                                                <td>{(page - 1) * limit + index + 1}</td>
                                                <td>
                                                    <div className="d-flex flex-column">
                                                        <span className="fw-bold">{institution.institutionName}</span>
                                                        <small className="text-muted">{institution.institutionCode}</small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <Badge color="primary">{institution.category}</Badge>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column">
                                                        <span>{institution.city}</span>
                                                        <small className="text-muted">{institution.state}</small>
                                                    </div>
                                                </td>
                                                <td className="text-center">
                                                    <Badge color={institution.programCount === 0 ? "danger" : "success"}>
                                                        {institution.programCount}
                                                    </Badge>
                                                </td>
                                                <td className="text-center">
                                                    <Badge color={institution.courseCount === 0 ? "danger" : "success"}>
                                                        {institution.courseCount}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <Badge
                                                        color={
                                                            institution.verifiedStatus === "approved"
                                                                ? "success"
                                                                : institution.verifiedStatus === "rejected"
                                                                    ? "danger"
                                                                    : "warning"
                                                        }
                                                    >
                                                        {institution.verifiedStatus}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-2 flex-wrap">
                                                        <Button
                                                            color="primary"
                                                            size="sm"
                                                            onClick={() => handleAddProgram(institution)}
                                                            title="Add Program"
                                                        >
                                                            <i className="mdi mdi-plus" /> Program
                                                        </Button>
                                                        <Button
                                                            color="success"
                                                            size="sm"
                                                            onClick={() => handleAddCourse(institution)}
                                                            disabled={institution.programCount === 0}
                                                            title={
                                                                institution.programCount === 0
                                                                    ? "Add a program first"
                                                                    : "Add Course"
                                                            }
                                                        >
                                                            <i className="mdi mdi-plus" /> Course
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>

                                {/* Pagination */}
                                <div className="pagination-container d-flex align-items-center justify-content-between mt-3">
                                    <div>
                                        Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalRecords)} of {totalRecords} entries
                                    </div>
                                    <Pagination>
                                        <PaginationItem disabled={page <= 1}>
                                            <PaginationLink previous onClick={() => setPage(page - 1)} />
                                        </PaginationItem>

                                        {[...Array(Math.min(5, totalPages))].map((_, index) => {
                                            const pageNumber = index + 1
                                            return (
                                                <PaginationItem key={pageNumber} active={pageNumber === page}>
                                                    <PaginationLink onClick={() => setPage(pageNumber)}>
                                                        {pageNumber}
                                                    </PaginationLink>
                                                </PaginationItem>
                                            )
                                        })}

                                        {totalPages > 5 && page < totalPages - 2 && (
                                            <PaginationItem disabled>
                                                <PaginationLink>...</PaginationLink>
                                            </PaginationItem>
                                        )}

                                        {totalPages > 5 && (
                                            <PaginationItem active={page === totalPages}>
                                                <PaginationLink onClick={() => setPage(totalPages)}>
                                                    {totalPages}
                                                </PaginationLink>
                                            </PaginationItem>
                                        )}

                                        <PaginationItem disabled={page >= totalPages}>
                                            <PaginationLink next onClick={() => setPage(page + 1)} />
                                        </PaginationItem>
                                    </Pagination>
                                </div>
                            </>
                        )}
                    </div>
                </Col>
            </Row>
        </div>
    )
}

export default DataCompletionDashboard
